'use client';
import React, { useState } from 'react';
import { AlertTriangle, Copy, CheckCheck, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import type { RootCauseResult } from './AIDiagnosisContent';
import { toast } from 'sonner';

interface RootCauseCardProps {
  result: RootCauseResult;
}

const CATEGORY_LABELS: Record<string, { label: string; color: string }> = {
  config: { label: 'Configuration', color: '#f59e0b' },
  memory_leak: { label: 'Memory Leak', color: '#ef4444' },
  network: { label: 'Network', color: '#3b82f6' },
  dependency: { label: 'Dependency', color: '#8b5cf6' },
  capacity: { label: 'Capacity', color: '#f97316' },
  database: { label: 'Database', color: '#06b6d4' },
  security: { label: 'Security', color: '#ef4444' },
};

export default function RootCauseCard({ result }: RootCauseCardProps) {
  const [copiedCmd, setCopiedCmd] = useState<string | null>(null);
  const cat = CATEGORY_LABELS[result.category] || { label: result.category, color: '#64748b' };

  const copyCommand = async (cmd: string) => {
    await navigator.clipboard.writeText(cmd);
    setCopiedCmd(cmd);
    setTimeout(() => setCopiedCmd(null), 2000);
    toast.success('Command copied to clipboard');
  };

  return (
    <div
      style={{
        backgroundColor: '#0d1117',
        border: '1px solid rgba(139,92,246,0.3)',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 0 24px rgba(139,92,246,0.08)',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid #1e293b',
          backgroundColor: 'rgba(139,92,246,0.06)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: 'rgba(139,92,246,0.15)',
              border: '1px solid rgba(139,92,246,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AlertTriangle size={15} style={{ color: '#8b5cf6' }} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span
                className="font-mono"
                style={{
                  fontSize: '9px',
                  fontWeight: 700,
                  color: '#8b5cf6',
                  letterSpacing: '0.1em',
                }}
              >
                ROOT CAUSE IDENTIFIED
              </span>
              <span
                className="font-mono"
                style={{
                  fontSize: '9px',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  backgroundColor: `${cat.color}15`,
                  color: cat.color,
                  border: `1px solid ${cat.color}30`,
                }}
              >
                {cat.label.toUpperCase()}
              </span>
            </div>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#e2e8f0', lineHeight: 1.4 }}>
              {result.summary}
            </p>
          </div>
        </div>

        {/* Confidence */}
        <div style={{ flexShrink: 0, textAlign: 'right' }}>
          <div
            className="font-mono tabular-nums"
            style={{
              fontSize: '28px',
              fontWeight: 700,
              color: result.confidence >= 90 ? '#10b981' : result.confidence >= 70 ? '#f59e0b' : '#ef4444',
              lineHeight: 1,
            }}
          >
            {result.confidence}%
          </div>
          <div className="font-mono" style={{ fontSize: '9px', color: '#64748b', letterSpacing: '0.08em', marginTop: '2px' }}>
            CONFIDENCE
          </div>
          <div
            style={{
              marginTop: '6px',
              width: '80px',
              height: '4px',
              backgroundColor: '#161b22',
              borderRadius: '2px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${result.confidence}%`,
                backgroundColor: result.confidence >= 90 ? '#10b981' : result.confidence >= 70 ? '#f59e0b' : '#ef4444',
                borderRadius: '2px',
                transition: 'width 800ms ease',
              }}
            />
          </div>
        </div>
      </div>

      <div style={{ padding: '20px' }}>
        {/* Detail */}
        <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#94a3b8', lineHeight: 1.7 }}>
          {result.detail}
        </p>

        {/* Affected Services */}
        <div style={{ marginBottom: '20px' }}>
          <div
            className="font-mono"
            style={{ fontSize: '10px', fontWeight: 600, color: '#64748b', letterSpacing: '0.1em', marginBottom: '8px' }}
          >
            AFFECTED SERVICES
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {result.affectedServices.map((svc) => (
              <span
                key={`svc-${svc}`}
                className="font-mono"
                style={{
                  fontSize: '11px',
                  padding: '3px 8px',
                  borderRadius: '4px',
                  backgroundColor: 'rgba(239,68,68,0.08)',
                  color: '#fca5a5',
                  border: '1px solid rgba(239,68,68,0.2)',
                }}
              >
                {svc}
              </span>
            ))}
          </div>
        </div>

        {/* Evidence Panel */}
        <div style={{ marginBottom: '20px' }}>
          <div
            className="font-mono"
            style={{ fontSize: '10px', fontWeight: 600, color: '#64748b', letterSpacing: '0.1em', marginBottom: '8px' }}
          >
            EVIDENCE ({result.evidence.length} SOURCES)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {result.evidence.map((ev, idx) => (
              <div
                key={`ev-${idx}`}
                style={{
                  display: 'flex',
                  gap: '10px',
                  padding: '10px 12px',
                  backgroundColor: '#161b22',
                  border: '1px solid #1e293b',
                  borderRadius: '6px',
                  borderLeft: `3px solid ${ev.color}`,
                }}
              >
                <span
                  className="font-mono"
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    color: ev.color,
                    flexShrink: 0,
                    minWidth: '90px',
                  }}
                >
                  {ev.source}
                </span>
                <span
                  className="font-mono"
                  style={{ fontSize: '11px', color: '#94a3b8', lineHeight: 1.5 }}
                >
                  {ev.finding}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Suggested Actions */}
        <div style={{ marginBottom: '20px' }}>
          <div
            className="font-mono"
            style={{ fontSize: '10px', fontWeight: 600, color: '#64748b', letterSpacing: '0.1em', marginBottom: '8px' }}
          >
            SUGGESTED REMEDIATION COMMANDS
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {result.suggestedActions.map((cmd, idx) => (
              <div
                key={`cmd-${idx}`}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '10px 12px',
                  backgroundColor: '#0a0e17',
                  border: '1px solid #1e293b',
                  borderRadius: '6px',
                }}
              >
                <span
                  className="font-mono"
                  style={{ fontSize: '9px', color: '#374151', marginTop: '2px', flexShrink: 0 }}
                >
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <code
                  className="font-mono"
                  style={{
                    fontSize: '11px',
                    color: '#a5f3fc',
                    flex: 1,
                    wordBreak: 'break-all',
                    lineHeight: 1.5,
                  }}
                >
                  {cmd}
                </code>
                <button
                  onClick={() => copyCommand(cmd)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: copiedCmd === cmd ? '#10b981' : '#374151',
                    padding: '2px',
                    flexShrink: 0,
                    transition: 'color 150ms',
                  }}
                >
                  {copiedCmd === cmd ? <CheckCheck size={13} /> : <Copy size={13} />}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <Link
          href="/agentic-resolution"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '12px 20px',
            backgroundColor: '#8b5cf6',
            borderRadius: '8px',
            textDecoration: 'none',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 600,
            transition: 'all 150ms',
            boxShadow: '0 0 20px rgba(139,92,246,0.3)',
          }}
        >
          Proceed to Agentic Resolution
          <ArrowRight size={15} />
        </Link>
      </div>
    </div>
  );
}